import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/auth';
import { useData } from '@/contexts/data';
import { userService } from '@/services/user';
import { getAccessRole } from '@/utils/rbac';
import type {
  BulkConfirmResponse,
  BulkImportStep,
  BulkPreviewData,
  BulkUserType,
  EditableBulkRow,
} from '../types';
import {
  buildConfirmPayload,
  calculateRowSummary,
  getBulkConfirmErrorMessage,
  getBulkImportErrorMessage,
  isCsvFile,
  mapPreviewRowsToEditable,
  mergeRowErrors,
  validateRowByType,
} from '../utils';

export function useBulkUserImport(onImported?: () => void) {
  const { user: currentUser } = useAuth();
  const accessRole = getAccessRole(currentUser);
  const { partners, partnersLoading, partnersError } = useData();
  const [step, setStep] = useState<BulkImportStep>('setup');
  const [userType, setUserType] = useState<BulkUserType>('internal');
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [setupError, setSetupError] = useState('');
  const [rows, setRows] = useState<EditableBulkRow[]>([]);
  const [result, setResult] = useState<BulkConfirmResponse | null>(null);
  const [previewBrands, setPreviewBrands] = useState<Array<{ id: string; name: string }>>([]);
  const [pendingUserType, setPendingUserType] = useState<BulkUserType | null>(null);
  const [isTypeChangeConfirmOpen, setIsTypeChangeConfirmOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [rowPendingRemoval, setRowPendingRemoval] = useState<EditableBulkRow | null>(null);
  const [editingRow, setEditingRow] = useState<EditableBulkRow | null>(null);
  const isConfirmingRef = useRef(false);

  const brandOptions = useMemo(
    () => partners.map((partner) => ({ id: partner.id, name: partner.name })),
    [partners]
  );

  const summary = useMemo(() => calculateRowSummary(rows), [rows]);

  const hasPreviewData = rows.length > 0 || file !== null;

  const resetImportState = useCallback(() => {
    setStep('setup');
    setFile(null);
    setFileError('');
    setSetupError('');
    setRows([]);
    setResult(null);
    setPreviewBrands([]);
    setEditingRow(null);
    setRowPendingRemoval(null);
    setIsConfirmOpen(false);
  }, []);

  const handleUserTypeChange = (nextType: BulkUserType) => {
    if (nextType === userType) return;
    if (hasPreviewData) {
      setPendingUserType(nextType);
      setIsTypeChangeConfirmOpen(true);
      return;
    }
    setUserType(nextType);
  };

  const confirmUserTypeChange = () => {
    if (!pendingUserType) return;
    setUserType(pendingUserType);
    setPendingUserType(null);
    setIsTypeChangeConfirmOpen(false);
    setFile(null);
    setFileError('');
    setSetupError('');
    setRows([]);
    setResult(null);
    setPreviewBrands([]);
    setStep('setup');
  };

  const cancelUserTypeChange = () => {
    setPendingUserType(null);
    setIsTypeChangeConfirmOpen(false);
  };

  const handleFileChange = (nextFile: File | null) => {
    setFileError('');
    setSetupError('');
    if (!nextFile) {
      setFile(null);
      return;
    }
    if (!isCsvFile(nextFile)) {
      setFile(null);
      setFileError('O formato do arquivo é inválido. Selecione um arquivo com extensão .csv.');
      return;
    }
    setFile(nextFile);
  };

  const handlePreview = async () => {
    setSetupError('');
    setFileError('');

    if (!userType) {
      setSetupError('Selecione o tipo de usuário.');
      return;
    }

    if (selectedBrandIds.length === 0) {
      setSetupError('Selecione pelo menos uma marca.');
      return;
    }

    if (!file) {
      setFileError('Selecione um arquivo CSV para continuar.');
      return;
    }

    if (!isCsvFile(file)) {
      setFileError('O formato do arquivo é inválido. Selecione um arquivo com extensão .csv.');
      return;
    }

    try {
      setStep('processing');
      const preview = await userService.previewBulkUsers({
        file,
        userType,
        brandIds: selectedBrandIds,
      });
      setPreviewBrands(preview.brands ?? []);
      setRows(mapPreviewRowsToEditable(preview.rows ?? [], userType, accessRole));
      setStep('review');
    } catch (error) {
      setStep('setup');
      setSetupError(getBulkImportErrorMessage(error));
    }
  };

  const handleEditRow = (rowId: string, data: BulkPreviewData) => {
    setRows((current) =>
      current.map((row) => {
        if (row.id !== rowId) return row;
        const localErrors = validateRowByType(userType, data, accessRole);
        const remoteErrors = row.errors.filter((error) => error.source === 'remote');
        const mergedErrors = mergeRowErrors(remoteErrors, localErrors, data, row.data);
        return {
          ...row,
          data,
          edited: true,
          errors: mergedErrors,
          status: mergedErrors.length > 0 ? 'invalid' : 'valid',
        };
      })
    );
    setEditingRow(null);
  };

  const goBackToSetup = () => {
    setStep('setup');
    setFile(null);
    setFileError('');
    setSetupError('');
    setRows([]);
    setPreviewBrands([]);
    setResult(null);
    setEditingRow(null);
    setRowPendingRemoval(null);
    setIsConfirmOpen(false);
  };

  const requestRemoveRow = (row: EditableBulkRow) => {
    setRowPendingRemoval(row);
  };

  const confirmRemoveRow = () => {
    if (!rowPendingRemoval) return;
    setRows((current) =>
      current.map((row) =>
        row.id === rowPendingRemoval.id ? { ...row, removed: true } : row
      )
    );
    setRowPendingRemoval(null);
  };

  const cancelRemoveRow = () => {
    setRowPendingRemoval(null);
  };

  const handleConfirmImport = async () => {
    if (summary.sendableRows === 0 || isConfirmingRef.current) return;

    try {
      isConfirmingRef.current = true;
      setIsConfirmOpen(false);
      setStep('confirming');
      const payload = buildConfirmPayload(userType, selectedBrandIds, rows);
      const response = await userService.confirmBulkUsers(payload);
      setResult(response);
      setStep('result');
      onImported?.();
    } catch (error) {
      setStep('review');
      setSetupError(getBulkConfirmErrorMessage(error));
    } finally {
      isConfirmingRef.current = false;
    }
  };

  const startNewImport = () => {
    resetImportState();
    setSelectedBrandIds([]);
    setUserType('internal');
  };

  return {
    step,
    userType,
    selectedBrandIds,
    setSelectedBrandIds,
    file,
    fileError,
    setupError,
    rows,
    result,
    previewBrands,
    brandOptions,
    partnersLoading,
    partnersError,
    summary,
    pendingUserType,
    isTypeChangeConfirmOpen,
    isConfirmOpen,
    setIsConfirmOpen,
    rowPendingRemoval,
    editingRow,
    setEditingRow,
    handleUserTypeChange,
    confirmUserTypeChange,
    cancelUserTypeChange,
    handleFileChange,
    handlePreview,
    handleEditRow,
    requestRemoveRow,
    confirmRemoveRow,
    cancelRemoveRow,
    handleConfirmImport,
    startNewImport,
    goBackToSetup,
    resetImportState,
  };
}
